
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import API_URL from '../../api/APIUrl';
import { useAuthContext } from '../../adminlogin/useAuthContext';
import SideMenu from '../SideMenu';

const ProcessApplication = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { adminuser } = useAuthContext();

    const [student, setStudent] = useState(null);
    const [adminStatus, setAdminStatus] = useState('PENDING');
    const [adminComment, setAdminComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!adminuser?.token || !id) return;

        const fetchStudent = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await fetch(
                    `${API_URL.GET_SINGLE_STUDENT_BY_ID}${id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${adminuser.token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Unable to load application');
                }

                setStudent(data);
                setAdminStatus(data.admin_status || 'PENDING');
                setAdminComment(data.admin_comment || '');

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchStudent();
    }, [id, adminuser?.token]);

    const handleSaveStatus = async (e) => {
        e.preventDefault();

        if (!['PENDING', 'APPROVED', 'REJECTED'].includes(adminStatus)) {
            setError('Please select a valid application status.');
            return;
        }

        const adminId = adminuser?.email || adminuser?._id || adminuser?.id;

        if (!adminId) {
            setError('Admin identification is missing. Please log in again.');
            return;
        }

        try {
            setSaving(true);
            setError('');

            const response = await fetch(
                `${API_URL.PATCH_STUDENT_ADMIN_STATUS}/${id}`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${adminuser.token}`
                    },
                    body: JSON.stringify({
                        admin_status: adminStatus,
                        admin_comment: adminComment,
                        admin_id: adminId
                    })
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Unable to update application');
            }

            setStudent(prev => ({
                ...prev,
                admin_status: adminStatus,
                admin_comment: adminComment,
                admin_id: adminId
            }));

            alert(data.message || 'Application status saved successfully!');

        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-IN');
    };

    const displayValue = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return value;
    };

    const photoUrl = (photo) => {
        if (!photo) return null;

        if (photo.startsWith('http://') || photo.startsWith('https://')) {
            return photo;
        }

        return `/${photo.replace(/^\.?\//, '')}`;
    };

    const Detail = ({ label, value }) => (
        <div className="col-md-6 mb-3">
            <small className="text-muted d-block">{label}</small>
            <strong>{displayValue(value)}</strong>
        </div>
    );

    if (!adminuser?.token) {
        return <div className="container mt-4">Please log in as an administrator.</div>;
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-md-2">
                    <SideMenu />
                </div>

                <div className="col-md-10 p-4">

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3>Student Application Details</h3>
                        <button
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </button>
                    </div>

                    {loading && <p>Loading application details...</p>}

                    {error && (
                        <div className="alert alert-danger">{error}</div>
                    )}

                    {!loading && student && (
                        <>
                            {/* Personal Information */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <strong>Personal Information</strong>
                                </div>

                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-3 mb-3">
                                            {student.photo ? (
                                                <img
                                                    src={photoUrl(student.photo)}
                                                    alt="Student"
                                                    style={{
                                                        width: '130px',
                                                        height: '150px',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            ) : (
                                                <div className="text-muted">
                                                    No photograph available
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-9">
                                            <div className="row">
                                                <Detail label="Registration Number" value={student.code} />
                                                <Detail label="First Name" value={student.firstname} />
                                                <Detail label="Middle Name" value={student.middlename} />
                                                <Detail label="Surname" value={student.surname} />
                                                <Detail label="Gender" value={student.gender} />
                                                <Detail label="Date of Birth" value={formatDate(student.dob)} />
                                                <Detail label="Religion" value={student.religion} />
                                                <Detail label="Caste" value={student.caste} />
                                                <Detail label="Sub Caste" value={student.sub_caste} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <strong>Contact Information</strong>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <Detail label="Email" value={student.email} />
                                        <Detail label="Student WhatsApp" value={student.student_whatsapp} />
                                        <Detail label="Father WhatsApp" value={student.father_whatsapp} />
                                        <Detail label="Mother WhatsApp" value={student.mother_whatsapp} />
                                    </div>
                                </div>
                            </div>

                            {/* Address Information */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <strong>Address Information</strong>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <Detail label="State" value={student.state} />
                                        <Detail label="City" value={student.city} />
                                        <Detail label="PIN Code" value={student.zipcode} />
                                        <Detail label="Current Address" value={student.current_address} />
                                        <Detail label="Permanent Address" value={student.permanent_address} />
                                    </div>
                                </div>
                            </div>

                            {/* Education Information */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <strong>Education Information</strong>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <Detail label="Education Category" value={student.education_category} />
                                        <Detail label="Institution Name" value={student.institution_name} />
                                        <Detail
                                            label="Financial Help Required For"
                                            value={student.need_financial_help_for}
                                        />
                                        <Detail
                                            label="Last 3 Years Academic Performance"
                                            value={student.last_3_years_academic_performance}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Financial Information */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <strong>Financial Information</strong>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <Detail label="Total Fees" value={student.total_fees} />
                                        <Detail label="Arranged Amount" value={student.arranged_amount} />
                                        <Detail
                                            label="Balance Amount"
                                            value={
                                                Number(student.total_fees || 0) -
                                                Number(student.arranged_amount || 0)
                                            }
                                        />
                                        <Detail
                                            label="Payment Deadline"
                                            value={formatDate(student.payment_deadline)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Application Review */}
                            <div className="card mb-4 border-primary">
                                <div className="card-header bg-light">
                                    <strong>Admin Application Review</strong>
                                </div>

                                <div className="card-body">
                                    <form onSubmit={handleSaveStatus}>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Application Status
                                            </label>

                                            <select
                                                className="form-select"
                                                value={adminStatus}
                                                onChange={(e) => setAdminStatus(e.target.value)}
                                                required
                                            >
                                                <option value="PENDING">PENDING</option>
                                                <option value="APPROVED">APPROVED</option>
                                                <option value="REJECTED">REJECTED</option>
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">
                                                Admin Comments
                                            </label>

                                            <textarea
                                                className="form-control"
                                                rows="4"
                                                value={adminComment}
                                                onChange={(e) => setAdminComment(e.target.value)}
                                                placeholder="Enter application review comments..."
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={saving}
                                        >
                                            {saving ? 'Saving...' : 'Save Application Status'}
                                        </button>
                                    </form>
                                </div>
                            </div>

                            <div className="text-muted mb-3">
                                Application submitted: {formatDate(student.createdAt)}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProcessApplication;
